import { Injectable, inject } from '@angular/core';
import { HEADERS } from '../api-headers';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { LoadSuccessParams, SortDirection } from 'ag-grid-community';

import {
  USER_GET_ACCESSIBLE_FEED_TYPES,
  USER_POST_ADD_RECENT_OBJECT,
  USER_POST_CHANGE_PASSWORD,
  USER_POST_UNSELECT_ITEMS,
  USER_POST_UPDATE,
  USER_PUT_CREATE,
  USER_GET_RECIPIENTS,
  USER_GET_FEED_BY_ID,
  USER_POST_ALERTS,
  USER_POST_DASHBOARDS,
  USER_POST_DIMENSIONS,
  USER_POST_FEEDS,
  USER_POST_SCHEDULES,
  USER_POST_AUTO_STORY_RUNS,
  USER_POST_DELETE_AUTO_STORY_RUN,
  UTILS_DATA_OBJECT_GET_LIST_USERS,
  USER_POST_SELECT_ITEMS,
  USER_POST_SEND_FORGOTTEN_PASSWORD_EMAIL,
  USER_POST_SEND_PLATFORM_INVITATION,
  USER_POST_GET_PLATFORM_INVITATION_LINK,
  USER_POST_MENU_STATE,
  USER_POST_LANGUAGE,
  USER_POST_SWITCH_COMPANY,
  USER_POST_MOVE_COMPANY_PLAN,
  USER_POST_MOVE_COMPANY_EXECUTE,
  USER_POST_MOVE_COMPANY_CANCEL,
  USER_POST_CLEAR_FEED_SELECTED_ITEMS,
  USER_POST_DARK_MODE,
  USER_POST_OBJECTS,
  USER_POST_OBJECT_LIST,
  USER_DELETE_EXTERNAL_ACCOUNTS,
  USER_GET,
  USER_GET_ALERTS,
  USER_GET_CLIPPING_FEEDS,
  USER_GET_DASHBOARD_BY_ID,
  USER_GET_EXTERNAL_ACCOUNTS,
  USER_GET_NOTIFICATION_LIST,
  USER_GET_CHECK_FOR_NEW_ALERTS,
  USER_GET_NOTIFICATION_LIST_COUNT,
  USER_GET_GET_RECENT_OBJECTS,
  USER_GET_WORKSPACES,
  USER_GET_WORKSPACE_OBJECTS,
  USER_GET_ACTIVATED_USER_COMPANIES,
  USER_GET_RECENT_SEARCHES,
  USER_POST_ADD_RECENT_SEARCH,
  USER_POST_REMOVE_RECENT_SEARCH,
  USER_POST_SAVE_CONTEXT_VALUE,
  USER_POST_IMPORT_CSV,
  USER_POST_LIST_COMPANY_OBJECTS,
  USER_GET_AVAILABLE_GROUPS,
  USER_GET_AVAILABLE_ROLES,
  USER_GET_AVAILABLE_COMPANIES,
  USER_GET_AVAILABLE_ACCOUNT_MANAGERS,
  USER_GET_BY_INVITE_TOKEN,
  USER_GET_BY_RESET_TOKEN,
  USER_POST_ACTIVATE_ACCOUNT,
  USER_POST_RESET_PASSWORD,
} from '../endpoints/user.endpoints';
import { ObjectContextSettings } from 'src/app/core/models/object.models';
import { toQueryParams } from '../api-utils';

import {
  User,
  Feed,
  Workspace,
  Dashboard,
  DataObject,
  ExternalService,
  NomenclatureItem,
  ObjectDefinition,
  Post,
  UserObjectsResponse,
  PermittedCompanyItem,
  RecentSearch,
  RecentSearchPayload,
  // The app model, not the DOM's global `Notification`.
  Notification,
} from 'src/app/core/models/core.models';
import { Alert } from 'src/app/core/models/alert.models';

import { catchError, from, map, Observable, of, switchMap } from 'rxjs';

/** Strips the `data:…;base64,` prefix a `FileReader` data URL carries. */
function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** The body shape both `select_items` and `unselect_items` take. */
function selectedItemsFormData(feedId: string, posts: Post[]): FormData {
  const formData = new FormData();

  posts.forEach((post, index) => {
    formData.append(`items[${index}][object_id]`, post.id);
    formData.append(`items[${index}][feed_id]`, feedId);
    formData.append(
      `items[${index}][time_published]`,
      post.publish_time.timestamp.toString(),
    );
  });

  return formData;
}

export interface FeedAvailability {
  type_id: number;
  title: string;
  description: string;
  left: number;
  icon: string;
}

export interface ExternalServiceAccount {
  id: number;
  name: string;
  is_active: boolean;
  time_expire: number | null;
}

interface Recipient {
  text: string;
}

interface IdNom {
  id: number;
  name?: string;
}

export interface ManagedUserData {
  id?: string;
  password?: string | null;
  password_confirm?: string | null;
  company_id: string | null;
  display_name: string;
  email: string;
  email_alerts?: string | null;
  groups?: string[];
  roles?: string[];
  avatar_base64?: string | null;
  timezone?: IdNom | null;
  language?: IdNom | null;
  push_services?: string[];
  list_company_objects?: boolean;
  companies?: Array<{ id: string }>;
}

export interface SendPlatformInvitationResponse {
  emailIsSent: boolean;
  user: User;
}

export interface PlatformInvitationLinkResponse {
  link: string;
}

export interface SuccessResponse {
  success: boolean;
  message?: string;
}

/** POST /user/import_csv - per-row outcome of a CSV/XLSX user import. */
export interface ImportUsersResult {
  success: boolean;
  successCount: number;
  failureCount: number;
}

/** POST /user/list_company_objects - the server echoes back the stored flag. */
export interface ListCompanyObjectsResponse {
  user_id: number;
  list_company_objects: boolean;
}

export interface ActivatedUserCompaniesResult {
  companies: PermittedCompanyItem[];
  total: number;
}

/**
 * Criteria of an object search (POST /user/user_object_list), and the identity of
 * that search as a query key. `objectClassIds: null` is a real value ("no class
 * filter", what the post-related dialog sends), so it is never defaulted away.
 */
export interface UserObjectSearchCriteria {
  query: string;
  workspaceId: string | null;
  objectClassIds: number[] | null;
  objectTypeIds: number[] | null;
  resolveWorkspace?: boolean;
  sortField?: string;
  sortDirection?: SortDirection;
  /** Server-side row cap; the endpoint defaults to "all" when absent. */
  itemsCount?: number;
  /**
   * Picker rows: the server keeps owner, shared_with, can_edit, can_view and
   * lifecycle_status and skips the other permission flags, alerts and combined-feed
   * children. Lean rows are list rows only - never a substitute for an object
   * loaded by id (architecture.md, "Row models and editor dialogs").
   */
  lean?: boolean;
}

/** Owned-object counts per lifecycle bucket, keyed by object class (`workspace`, `query`, ...). */
export interface UserCompanyMoveCounts {
  [classKey: string]: { active: number; archived: number; deleted: number };
}

export interface UserCompanyMoveWarnings {
  /** Sessions the move signs out. */
  sessions: number;
  /** Names of the source-company groups the user leaves. */
  groups: string[];
  roles_dropped: number[];
  roles_kept: number[];
  /** Shares that stop being visible after the move. */
  invisible_shares: number;
  left_behind: { rule: number };
}

export interface UserCompanyMoveFailure {
  action_id: string;
  action_type_id: number;
  object_id: string;
  reason: string;
}

export interface UserCompanyMoveResults {
  applied: number;
  skipped: number;
  failed: number;
  failures: UserCompanyMoveFailure[];
}

/** `run_status_id` values of a company-move run (`results` is null until executed). */
export enum UserCompanyMoveRunStatus {
  Executed = 3,
  Cancelled = 4,
  Failed = 5,
}

/** The report POST /user/move_company_plan and /user/move_company_execute answer with. */
export interface UserCompanyMoveReport {
  run_id: string;
  run_status_id: number;
  user_id: string;
  source_company_id: string;
  target_company_id: string;
  new_owner_id: string | null;
  /** The user owns objects that must pass to an enabled user of the source company. */
  requires_new_owner: boolean;
  counts: UserCompanyMoveCounts;
  warnings: UserCompanyMoveWarnings;
  results: UserCompanyMoveResults | null;
}

export interface UserCompanyMoveCancelResponse {
  run_id: string;
  run_status_id: number;
}

export interface ExecuteCompanyMoveOptions {
  runId: string;
  /** Required by the server when the plan reported `requires_new_owner`. */
  newOwnerId?: string;
}

// TODO: We will pass typized objects to the services containing all inputs
@Injectable({ providedIn: 'root' })
export class UserApiService {
  private readonly http = inject(HttpClient);

  getAvailableFeedTypes(userId: string): Observable<FeedAvailability[]> {
    return this.http.get<FeedAvailability[]>(
      USER_GET_ACCESSIBLE_FEED_TYPES(userId),
    );
  }

  getActivatedUserCompanies(
    query: string,
    showAll: boolean,
  ): Observable<ActivatedUserCompaniesResult> {
    let params = new HttpParams().set('query', query);
    // showAll asks the server for the full list rather than the first page
    if (showAll) {
      params = params.set('startRow', 0).set('endRow', '10000');
    }

    return this.http
      .get<LoadSuccessParams>(USER_GET_ACTIVATED_USER_COMPANIES, { params })
      .pipe(
        map((response) => ({
          companies: (response.rowData ?? []) as PermittedCompanyItem[],
          total: response.rowCount ?? 0,
        })),
      );
  }

  getDashboard(userId: string, dashboardId: string): Observable<Dashboard> {
    const params = new HttpParams()
      .set('displayFullVersion', true)
      .set('id', dashboardId);
    return this.http.get<Dashboard>(USER_GET_DASHBOARD_BY_ID(userId), {
      params,
    });
  }

  listCompanyUsers(
    companyId: string,
  ): Observable<{ id: string; name: string; username: string }[]> {
    return this.http
      .get<any[]>(UTILS_DATA_OBJECT_GET_LIST_USERS, {
        params: { companyId },
      })
      .pipe(
        map((users) =>
          users.map((user) => ({
            id: user.id,
            name: user.name,
            username: user.username,
          })),
        ),
      );
  }

  getUserExternalAccounts(
    userId: string,
  ): Observable<ExternalServiceAccount[]> {
    return this.http.get<ExternalServiceAccount[]>(
      USER_GET_EXTERNAL_ACCOUNTS(userId),
    );
  }

  getUserClippingFeeds(): Observable<Feed[]> {
    return this.http.get<Feed[]>(USER_GET_CLIPPING_FEEDS);
  }

  getRecentObjects(): Observable<DataObject[]> {
    return this.http.get<DataObject[]>(USER_GET_GET_RECENT_OBJECTS);
  }

  getRecentSearches(): Observable<RecentSearch[]> {
    return this.http.get<RecentSearch[]>(USER_GET_RECENT_SEARCHES);
  }

  addRecentSearch(
    search: RecentSearchPayload,
  ): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      USER_POST_ADD_RECENT_SEARCH,
      search,
    );
  }

  removeRecentSearch(
    search: RecentSearchPayload,
  ): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      USER_POST_REMOVE_RECENT_SEARCH,
      search,
    );
  }

  /** User groups the given user may be assigned to, filtered by a search term. */
  getAvailableUserGroups(
    contextUserId: string,
    query: string,
  ): Observable<NomenclatureItem[]> {
    const httpParams = toQueryParams({ term: query, user_id: contextUserId });

    return this.http.get<NomenclatureItem[]>(USER_GET_AVAILABLE_GROUPS, {
      params: httpParams,
    });
  }

  /** User roles the given user may be assigned to, filtered by a search term. */
  getAvailableUserRoles(
    contextUserId: string,
    query: string,
  ): Observable<NomenclatureItem[]> {
    const httpParams = toQueryParams({ term: query, user_id: contextUserId });

    return this.http.get<NomenclatureItem[]>(USER_GET_AVAILABLE_ROLES, {
      params: httpParams,
    });
  }

  /**
   * Companies the given user may belong to, filtered by a search term.
   * @param includeExplicitlyPermitted TRUE to return requested user's explicitly permitted companies (respected if the current user is CA/GA/same as requested user)
   * @param includeImplicitlyPermitted TRUE to return requested user's implicitly permitted companies (respected if the current user is GA)
   */
  getAvailableUserCompanies(
    contextUserId: string,
    query: string,
    includeExplicitlyPermitted: boolean = true,
    includeImplicitlyPermitted: boolean = true,
    enabledOnly: boolean = false,
  ): Observable<NomenclatureItem[]> {
    const httpParams = toQueryParams({
      term: query,
      user_id: contextUserId,
      include_explicitly_permitted: includeExplicitlyPermitted,
      include_implicitly_permitted: includeImplicitlyPermitted,
      enabled_only: enabledOnly,
    });

    return this.http.get<NomenclatureItem[]>(USER_GET_AVAILABLE_COMPANIES, {
      params: httpParams,
    });
  }

  getAccountManagers(): Observable<User[]> {
    return this.http.get<User[]>(USER_GET_AVAILABLE_ACCOUNT_MANAGERS);
  }

  getUserByInviteToken(token: string): Observable<User | null> {
    return this.http
      .get<User>(USER_GET_BY_INVITE_TOKEN, {
        params: { token },
      })
      .pipe(
        catchError((error) => {
          console.error('Error fetching user by invite token:', error);
          return of(null); // Return null if there's an error
        }),
      );
  }

  getUserByResetToken(token: string): Observable<User | null> {
    return this.http
      .get<User>(USER_GET_BY_RESET_TOKEN, {
        params: { token },
      })
      .pipe(
        catchError((error) => {
          console.error('Error fetching user by reset token:', error);
          return of(null); // Return null if there's an error
        }),
      );
  }

  acceptPlatformInvitation(
    token: string,
    password: string,
  ): Observable<SuccessResponse> {
    const params = {
      activation_key: token,
      new_password: password,
    };
    return this.http.post<SuccessResponse>(USER_POST_ACTIVATE_ACCOUNT, params);
  }

  resetPassword(token: string, password: string): Observable<SuccessResponse> {
    const params = {
      reset_key: token,
      new_password: password,
    };
    return this.http.post<SuccessResponse>(USER_POST_RESET_PASSWORD, params);
  }

  getUser(userId: string): Observable<User> {
    return this.http.get<User>(USER_GET(userId));
  }

  getTriggeredAlerts(userId: string): Observable<Array<Alert>> {
    return this.http.get<Array<Alert>>(USER_GET_ALERTS(userId));
  }

  getAlertsCount(userId: string): Observable<number> {
    return this.http.get<number>(USER_GET_CHECK_FOR_NEW_ALERTS(userId));
  }

  getLatestNotifications(userId: string): Observable<Array<Notification>> {
    return this.http.get<Array<Notification>>(
      USER_GET_NOTIFICATION_LIST(userId),
    );
  }

  getNotificationsCount(userId: string): Observable<number> {
    return this.http.get<number>(USER_GET_NOTIFICATION_LIST_COUNT(userId));
  }

  /**
   * The user's workspaces. `withStats` is omitted entirely rather than sent as
   * `false` - `toQueryParams` skips undefined.
   */
  getUserWorkspaces(
    userId: string,
    lifecycleStatus: number[],
    withStats: boolean,
  ): Observable<Workspace[]> {
    return this.http.get<Workspace[]>(USER_GET_WORKSPACES(userId), {
      params: toQueryParams({
        'lifecycle_status[]': lifecycleStatus,
        with_stats: withStats ? true : undefined,
      }),
    });
  }

  /** The feeds / dashboards / dimensions of one workspace. */
  getUserWorkspaceObjects(
    workspaceId: string,
    lifecycleStatus: number[],
  ): Observable<UserObjectsResponse> {
    return this.http.get<UserObjectsResponse>(USER_GET_WORKSPACE_OBJECTS, {
      params: toQueryParams({
        workspace_id: workspaceId,
        'lifecycle_status[]': lifecycleStatus,
      }),
    });
  }

  /**
   * One feed by id. `displayFullVersion` is always sent `true`; only the
   * feed-results preview dialog asks for `resolveWorkspace`.
   */
  getFeed(
    userId: string,
    feedId: string,
    resolveWorkspace = false,
  ): Observable<Feed> {
    return this.http.get<Feed>(USER_GET_FEED_BY_ID(userId), {
      params: toQueryParams({
        display_full_version: true,
        id: feedId,
        resolve_workspace: resolveWorkspace,
      }),
    });
  }

  getEmailRecipentsSuggestions(userId: string): Observable<Recipient[]> {
    return this.http.get<Recipient[]>(USER_GET_RECIPIENTS(userId));
  }

  addRecentObject(object: ObjectDefinition) {
    return this.http.post(USER_POST_ADD_RECENT_OBJECT, object);
  }

  // ! camelCase keys are this endpoint's existing wire contract - do not snake_case
  saveContextValue(contextSettings: ObjectContextSettings): Observable<any> {
    return this.http.post<any>(USER_POST_SAVE_CONTEXT_VALUE, contextSettings);
  }

  /**
   * A batch of the user's objects by id. The backend does **not** preserve the
   * requested order - `injectFetchUserObjectsByIds` restores it.
   */
  getUserObjectsByIds(objectIds: string[]): Observable<DataObject[]> {
    return this.http.post<DataObject[]>(USER_POST_OBJECTS, { objectIds });
  }

  /**
   * Search the user's objects (the object pickers' and quick search's list).
   * `X-Skip-Loading` keeps the global loading indicator out of a keystroke-driven
   * search.
   */
  searchUserObjects(
    criteria: UserObjectSearchCriteria,
  ): Observable<DataObject[]> {
    const {
      query,
      workspaceId,
      objectClassIds,
      objectTypeIds,
      resolveWorkspace = true,
      sortField = 'title',
      sortDirection = 'asc',
      itemsCount,
      lean = false,
    } = criteria;

    return this.http.post<DataObject[]>(
      USER_POST_OBJECT_LIST,
      {
        query,
        workspaceId,
        objectClassIds,
        objectTypeIds,
        resolveWorkspace,
        sortField,
        sortDirection,
        itemsCount,
        lean,
      },
      { headers: new HttpHeaders({ 'X-Skip-Loading': 'true' }) },
    );
  }

  setDarkMode(darkMode: boolean) {
    return this.http.post(USER_POST_DARK_MODE, { dark_mode: darkMode });
  }

  /**
   * Drop the user's post selection in one feed. The body is `FormData`, not
   * url-encoded.
   */
  clearFeedSelectedItems(userId: string, feedId: string): Observable<boolean> {
    const body = new FormData();
    body.append('feed_id', feedId);

    return this.http.post<boolean>(
      USER_POST_CLEAR_FEED_SELECTED_ITEMS(userId),
      body,
    );
  }

  getUserAlertsAgGrid(gridParams): Observable<LoadSuccessParams> {
    const httpParams = toQueryParams({ output: 'table' });

    return this.http.post<LoadSuccessParams>(USER_POST_ALERTS, gridParams, {
      params: httpParams,
    });
  }

  getUserDashboardsAgGrid(gridParams): Observable<LoadSuccessParams> {
    const httpParams = toQueryParams({ output: 'table' });

    return this.http.post<LoadSuccessParams>(USER_POST_DASHBOARDS, gridParams, {
      params: httpParams,
    });
  }

  getUserFeedsAgGrid(gridParams): Observable<LoadSuccessParams> {
    const httpParams = toQueryParams({ output: 'table' });

    return this.http.post<LoadSuccessParams>(USER_POST_FEEDS, gridParams, {
      params: httpParams,
    });
  }

  getUserDimensionsAgGrid(gridParams): Observable<LoadSuccessParams> {
    const httpParams = toQueryParams({ output: 'table' });

    return this.http.post<LoadSuccessParams>(USER_POST_DIMENSIONS, gridParams, {
      params: httpParams,
    });
  }

  getUserSchedulesAgGrid(gridParams): Observable<LoadSuccessParams> {
    const httpParams = toQueryParams({ output: 'table' });

    return this.http.post<LoadSuccessParams>(USER_POST_SCHEDULES, gridParams, {
      params: httpParams,
    });
  }

  deleteAutoStoryRun(runId: number): Observable<{ success: boolean }> {
    const params = {
      run_id: runId.toString(),
    };
    return this.http.post<{ success: boolean }>(
      USER_POST_DELETE_AUTO_STORY_RUN,
      params,
    );
  }

  getUserAutoStoryRunsAgGrid(gridParams): Observable<LoadSuccessParams> {
    const httpParams = toQueryParams({ output: 'table' });

    return this.http.post<LoadSuccessParams>(
      USER_POST_AUTO_STORY_RUNS,
      gridParams,
      { params: httpParams },
    );
  }

  setUserLanguage(languageId: number): Observable<any> {
    return this.http.post(USER_POST_LANGUAGE, { language_id: languageId });
  }

  createUser(userData: ManagedUserData): Observable<User> {
    return this.http.put<User>(USER_PUT_CREATE, userData);
  }

  updateUser(userData: ManagedUserData): Observable<User> {
    return this.http.post<User>(USER_POST_UPDATE, userData);
  }

  setMenuStateOpen(menuStateOpen: boolean) {
    return this.http.post(USER_POST_MENU_STATE, {
      menu_state_open: menuStateOpen,
    });
  }

  sendForgottenPasswordEmail(
    email: string,
    resetType: number,
  ): Observable<any> {
    const body = new URLSearchParams();
    body.append('email', email);
    body.append('resetType', JSON.stringify(resetType));

    return this.http.post(USER_POST_SEND_FORGOTTEN_PASSWORD_EMAIL, body, {
      headers: HEADERS.formUrlEncoded,
    });
  }

  sendPlatformInvitation(
    userId: string,
  ): Observable<SendPlatformInvitationResponse> {
    const urlEncodeParams = new URLSearchParams();
    urlEncodeParams.append('userId', userId);

    return this.http.post<SendPlatformInvitationResponse>(
      USER_POST_SEND_PLATFORM_INVITATION,
      urlEncodeParams,
      {
        headers: HEADERS.formUrlEncoded,
      },
    );
  }

  getPlatformInvitationLink(
    userId: string,
  ): Observable<PlatformInvitationLinkResponse> {
    const body = new URLSearchParams({ userId }).toString();
    return this.http.post<PlatformInvitationLinkResponse>(
      USER_POST_GET_PLATFORM_INVITATION_LINK,
      body,
      {
        headers: HEADERS.formUrlEncoded,
      },
    );
  }

  switchCompany(companyId: string): Observable<User> {
    return this.http.post<User>(USER_POST_SWITCH_COMPANY, { companyId });
  }

  /** Plan a company move (POST /user/move_company_plan): a pending run plus its report. */
  planCompanyMove(
    userId: string,
    targetCompanyId: string,
  ): Observable<UserCompanyMoveReport> {
    return this.http.post<UserCompanyMoveReport>(USER_POST_MOVE_COMPANY_PLAN, {
      user_id: userId,
      target_company_id: targetCompanyId,
    });
  }

  /** Execute a planned move (POST /user/move_company_execute). */
  executeCompanyMove(
    options: ExecuteCompanyMoveOptions,
  ): Observable<UserCompanyMoveReport> {
    return this.http.post<UserCompanyMoveReport>(
      USER_POST_MOVE_COMPANY_EXECUTE,
      {
        run_id: options.runId,
        ...(options.newOwnerId ? { new_owner_id: options.newOwnerId } : {}),
      },
    );
  }

  /** Cancel a planned, not yet executed move (POST /user/move_company_cancel). */
  cancelCompanyMove(runId: string): Observable<UserCompanyMoveCancelResponse> {
    return this.http.post<UserCompanyMoveCancelResponse>(
      USER_POST_MOVE_COMPANY_CANCEL,
      { run_id: runId },
    );
  }

  /**
   * The two fields the "change someone else's password" form does not render go
   * out as the literal string `null`, which is what the backend expects - do not
   * swap in a params object that skips them.
   */
  changePassword(
    userId: string,
    currentPassword: string | null,
    newPassword: string | null,
    confirmPassword: string | null,
  ): Observable<SuccessResponse> {
    const body = new URLSearchParams();
    body.append('currentPassword', String(currentPassword));
    body.append('newPassword', String(newPassword));
    body.append('newPasswordConfirm', String(confirmPassword));
    body.append('userId', userId);

    return this.http.post<SuccessResponse>(
      USER_POST_CHANGE_PASSWORD(userId),
      body,
      { headers: HEADERS.formUrlEncoded },
    );
  }

  /**
   * Bulk user import. The file is read into a base64 payload field rather than
   * posted as multipart - the endpoint takes JSON.
   */
  importUsers(
    companyId: string,
    file: File,
    invite = false,
  ): Observable<ImportUsersResult> {
    return from(readFileAsBase64(file)).pipe(
      switchMap((base64) =>
        this.http.post<ImportUsersResult>(
          USER_POST_IMPORT_CSV,
          {
            csvFile: base64,
            companyId,
            fileName: file.name,
            invite,
          },
          { headers: HEADERS.json },
        ),
      ),
    );
  }

  listCompanyObjects(
    listCompanyObjects: boolean,
  ): Observable<ListCompanyObjectsResponse> {
    return this.http.post<ListCompanyObjectsResponse>(
      USER_POST_LIST_COMPANY_OBJECTS,
      { list_company_objects: listCompanyObjects },
    );
  }

  selectPosts(userId: string, feedId: string, posts: Post[]): Observable<any> {
    return this.http.post(
      USER_POST_SELECT_ITEMS(userId),
      selectedItemsFormData(feedId, posts),
    );
  }

  deSelectPosts(
    userId: string,
    feedId: string,
    posts: Post[],
  ): Observable<any> {
    return this.http.post(
      USER_POST_UNSELECT_ITEMS(userId),
      selectedItemsFormData(feedId, posts),
    );
  }

  unlinkExternalAccount(
    userId: string,
    service: ExternalService,
  ): Observable<any> {
    return this.http.delete(USER_DELETE_EXTERNAL_ACCOUNTS(userId), {
      body: { external_service_id: service },
    });
  }
}
